import React, { useState, useEffect } from 'react';
import {
  Pane,
  Card,
  Heading,
  Text,
  TextInput,
  Textarea,
  Button,
  Select,
  FileUploader,
  toaster,
  Spinner
} from 'evergreen-ui';
import { useProject } from '../context/ProjectContext';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';

const CreateFinding = () => {
  const { currentProject } = useProject();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    severity: 'medium',
    target: '',
    host_id: '',
    domain_id: '',
    port_id: '',
    url: '',
    status: 'open'
  });
  const [screenshots, setScreenshots] = useState([]);
  const [hosts, setHosts] = useState([]);
  const [domains, setDomains] = useState([]);
  const [ports, setPorts] = useState([]);
  const [returnToWstg, setReturnToWstg] = useState(false);

  // Check URL params and location state for pre-filling form
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const shouldReturn = searchParams.get('returnToWstg') === 'true';
    setReturnToWstg(shouldReturn);
    
    // Pre-fill form from location state
    if (location.state) {
      const { host_id, domain_id, test_id, test_name } = location.state;
      setFormData(prev => ({
        ...prev,
        host_id: host_id ? String(host_id) : '',
        domain_id: domain_id ? String(domain_id) : '',
        name: test_name ? `${test_name} - Finding` : prev.name,
        description: test_id ? `Finding for WSTG test: ${test_id}` : prev.description
      }));
    }
  }, [location]);

  useEffect(() => {
    if (currentProject?.id) {
      fetchEntities();
    }
  }, [currentProject?.id]);

  const fetchEntities = async () => {
    try {
      // Fetch hosts, domains, and ports for the project
      const [hostsRes, domainsRes, portsRes] = await Promise.all([
        api.get(`/projects/${currentProject.id}/results/hosts?limit=1000`),
        api.get(`/projects/${currentProject.id}/results/domains?limit=1000`),
        api.get(`/projects/${currentProject.id}/results/services?limit=1000`)
      ]);

      // APIs return different keys: hosts -> 'hosts', domains -> 'domains', services -> 'services'
      setHosts(hostsRes.data.hosts || []);
      setDomains(domainsRes.data.domains || []);
      setPorts(portsRes.data.services || []);
    } catch (error) {
      console.error('Error fetching entities:', error);
      toaster.danger('Failed to load entities');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (files) => {
    setScreenshots(files);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!currentProject?.id) {
      toaster.danger('Please select a project');
      return;
    }

    if (!formData.name || !formData.target) {
      toaster.danger('Name and Target are required');
      return;
    }

    setLoading(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('description', formData.description || '');
      formDataToSend.append('severity', formData.severity);
      formDataToSend.append('source', 'manual');
      formDataToSend.append('target', formData.target);
      formDataToSend.append('status', formData.status);
      
      if (formData.host_id) {
        formDataToSend.append('host_id', formData.host_id);
      }
      if (formData.domain_id) {
        formDataToSend.append('domain_id', formData.domain_id);
      }
      if (formData.port_id) {
        formDataToSend.append('port_id', formData.port_id);
      }
      if (formData.url) {
        formDataToSend.append('url', formData.url);
      }

      // Add screenshots
      screenshots.forEach((file) => {
        formDataToSend.append('screenshots', file);
      });

      const response = await api.post(
        `/projects/${currentProject.id}/findings`,
        formDataToSend,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      toaster.success('Finding created successfully!');
      
      // If we came from WSTG checklist, return there
      if (returnToWstg) {
        const searchParams = new URLSearchParams(location.search);
        const entityType = searchParams.get('entityType');
        const entityId = searchParams.get('entityId');
        
        if (entityType && entityId) {
          // Link the newly created finding to the WSTG item if testId is provided
          const testId = searchParams.get('testId');
          if (testId && response.data?.id) {
            try {
              const updateData = {
                test_id: testId,
                finding_id: response.data.id,
                host_id: formData.host_id || null,
                domain_id: formData.domain_id || null,
              };
              
              await api.post(
                `/projects/${currentProject.id}/wstg-checklist`,
                updateData
              );
            } catch (error) {
              console.error('Error linking finding to WSTG item:', error);
              // Don't show error, just continue
            }
          }
          
          // Navigate back to results with return params
          navigate(`/results?returnToWstg=true&entityType=${entityType}&entityId=${entityId}`);
          return;
        }
      }
      
      // Reset form
      setFormData({
        name: '',
        description: '',
        severity: 'medium',
        target: '',
        host_id: '',
        domain_id: '',
        port_id: '',
        url: '',
        status: 'open'
      });
      setScreenshots([]);
    } catch (error) {
      console.error('Error creating finding:', error);
      toaster.danger(error.response?.data?.detail || 'Failed to create finding');
    } finally {
      setLoading(false);
    }
  };

  if (!currentProject) {
    return (
      <Pane padding={32} textAlign="center">
        <Text>Please select a project first</Text>
      </Pane>
    );
  }

  const handleBack = () => {
    if (returnToWstg) {
      const searchParams = new URLSearchParams(location.search);
      const entityType = searchParams.get('entityType');
      const entityId = searchParams.get('entityId');
      
      if (entityType && entityId) {
        navigate(`/results?returnToWstg=true&entityType=${entityType}&entityId=${entityId}`);
        return;
      }
    }
    navigate('/results');
  };

  return (
    <Pane padding={32} maxWidth={800} marginX="auto" marginY={0}>
      <Pane display="flex" justifyContent="space-between" alignItems="center" marginBottom={24}>
        <Heading size={700}>Create Finding</Heading>
        {returnToWstg && (
          <Button appearance="minimal" onClick={handleBack}>
            ← Back to Checklist
          </Button>
        )}
      </Pane>
      
      <Card padding={24} elevation={1}>
        <form onSubmit={handleSubmit}>
          <Pane display="flex" flexDirection="column" gap={16}>
            {/* Name */}
            <Pane>
              <Text size={400} fontWeight={500} marginBottom={8} display="block">
                Finding Name *
              </Text>
              <TextInput
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Enter finding name"
                width="100%"
                required
              />
            </Pane>

            {/* Description */}
            <Pane>
              <Text size={400} fontWeight={500} marginBottom={8} display="block">
                Description
              </Text>
              <Textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Enter finding description"
                rows={4}
                width="100%"
              />
            </Pane>

            {/* Severity */}
            <Pane>
              <Text size={400} fontWeight={500} marginBottom={8} display="block">
                Severity
              </Text>
              <Select
                value={formData.severity}
                onChange={(e) => handleInputChange('severity', e.target.value)}
                width="100%"
              >
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
                <option value="info">Info</option>
              </Select>
            </Pane>

            {/* Target */}
            <Pane>
              <Text size={400} fontWeight={500} marginBottom={8} display="block">
                Target *
              </Text>
              <TextInput
                value={formData.target}
                onChange={(e) => handleInputChange('target', e.target.value)}
                placeholder="e.g., https://example.com or 192.168.1.1"
                width="100%"
                required
              />
            </Pane>

            {/* URL */}
            <Pane>
              <Text size={400} fontWeight={500} marginBottom={8} display="block">
                URL
              </Text>
              <TextInput
                value={formData.url}
                onChange={(e) => handleInputChange('url', e.target.value)}
                placeholder="Optional: specific URL where finding was found"
                width="100%"
              />
            </Pane>

            {/* Host */}
            <Pane>
              <Text size={400} fontWeight={500} marginBottom={8} display="block">
                Associated Host
              </Text>
              <Select
                value={formData.host_id || ''}
                onChange={(e) => handleInputChange('host_id', e.target.value)}
                width="100%"
              >
                <option value="">None</option>
                {hosts.map(host => (
                  <option key={host.id} value={host.id}>
                    {host.ip_address}
                  </option>
                ))}
              </Select>
            </Pane>

            {/* Domain */}
            <Pane>
              <Text size={400} fontWeight={500} marginBottom={8} display="block">
                Associated Domain
              </Text>
              <Select
                value={formData.domain_id || ''}
                onChange={(e) => handleInputChange('domain_id', e.target.value)}
                width="100%"
              >
                <option value="">None</option>
                {domains.map(domain => (
                  <option key={domain.id} value={domain.id}>
                    {domain.name}
                  </option>
                ))}
              </Select>
            </Pane>

            {/* Port */}
            <Pane>
              <Text size={400} fontWeight={500} marginBottom={8} display="block">
                Associated Port
              </Text>
              <Select
                value={formData.port_id || ''}
                onChange={(e) => handleInputChange('port_id', e.target.value)}
                width="100%"
              >
                <option value="">None</option>
                {ports.map(port => (
                  <option key={port.id} value={port.id}>
                    {port.host_ip || port.host?.ip_address || 'Unknown'}:{port.number}/{port.protocol}
                  </option>
                ))}
              </Select>
            </Pane>

            {/* Status */}
            <Pane>
              <Text size={400} fontWeight={500} marginBottom={8} display="block">
                Status
              </Text>
              <Select
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                width="100%"
              >
                <option value="open">Open</option>
                <option value="closed">Closed</option>
                <option value="false_positive">False Positive</option>
              </Select>
            </Pane>

            {/* Screenshots */}
            <Pane>
              <Text size={400} fontWeight={500} marginBottom={8} display="block">
                Screenshots
              </Text>
              <FileUploader
                accept="image/*"
                multiple
                onChange={handleFileChange}
                renderFileUploader={({ openFileDialog }) => (
                  <Button onClick={openFileDialog} appearance="default">
                    Select Screenshots
                  </Button>
                )}
              />
              {screenshots.length > 0 && (
                <Pane marginTop={8}>
                  <Text size={300} color="muted">
                    {screenshots.length} file(s) selected
                  </Text>
                </Pane>
              )}
            </Pane>

            {/* Submit Button */}
            <Pane display="flex" justifyContent="flex-end" marginTop={16}>
              <Button
                type="submit"
                appearance="primary"
                isLoading={loading}
                disabled={loading}
              >
                Create Finding
              </Button>
            </Pane>
          </Pane>
        </form>
      </Card>
    </Pane>
  );
};

export default CreateFinding;

